import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { ago, clockTime } from '@/lib/format';
import { now } from '@/lib/clock';
import { coordinates, mapsLink } from './artifact-meta';
import { Fact } from './parts';

type MapPoint = Extract<WireArtifact, { kind: 'map' }>;

const chip = 'absolute rounded-lg border border-line bg-bg px-2.5 py-1.5 font-mono text-[10.5px]';

/**
 * Where a vehicle was last reported. No route is drawn — the records carry one
 * point, not a path — so the grid holds a single pulsing position, and the
 * real map is one link away.
 */
export function MapArtifact({ artifact }: { artifact: MapPoint }): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div className="border-line bg-surf relative aspect-[4/3] overflow-hidden rounded-xl border bg-[linear-gradient(var(--line)_1px,transparent_1px),linear-gradient(90deg,var(--line)_1px,transparent_1px)] bg-[size:32px_32px]">
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="bg-acc absolute -inset-[11px] rounded-full opacity-20 [animation:orb-pulse_2s_ease-out_infinite]" />
          <span className="bg-acc border-bg relative block size-3.5 rounded-full border-[2.5px]" />
        </span>
        <div className={`${chip} text-mute bottom-3 left-3`}>
          {coordinates(artifact.lat, artifact.lon)}
        </div>
        <div className={`${chip} text-ok top-3 right-3`}>
          LIVE · {ago(artifact.at, now()).toUpperCase()}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        <Fact k="Speed" v={artifact.speedMph === undefined ? '—' : `${artifact.speedMph} mph`} />
        <Fact k="Reported" v={clockTime(artifact.at)} />
        <Fact k="Place" v={artifact.place ?? '—'} />
      </div>
      <a
        href={mapsLink(artifact.lat, artifact.lon)}
        target="_blank"
        rel="noopener noreferrer"
        className="border-line bg-surf text-ink hover:border-acc rounded-[9px] border p-[9px] text-center text-[13px]"
      >
        Open in Google Maps ↗
      </a>
    </div>
  );
}
